import { barsOf } from './score';
import { TEMPLATES } from './templates/index';

export type GenreId = 'lofi' | 'house' | 'trap' | 'amapiano' | 'synthwave' | 'ukg' | 'speedgarage' | 'phonk' | 'brphonk' | 'dnb';

export type WorldPlan = {
  n: number;
  genre: GenreId;
  bpm: readonly [lo: number, hi: number];
  levels: readonly [lo: number, hi: number];
  seconds: readonly [first: number, boss: number];
  titles: readonly [string, string, string, string, string];
};

export type LadderTrack = {
  world: number;
  index: number;
  boss: boolean;
  genre: GenreId;
  seed: string;
  title: string;
  bpm: number;
  bars: number;
  seconds: number;
  energy: number;
  level: number;
  spec: string;
  ready: boolean;
};

export const TRACKS_PER_WORLD = 5;

export const WORLD_PLANS: readonly WorldPlan[] = [
  { n: 1, genre: 'lofi', bpm: [80, 84], levels: [1, 1], seconds: [36, 50], titles: ['Chuva Fina', 'Papel Dobrado', 'Janela Aberta', 'Caneca Quente', 'Sono Leve'] },
  { n: 2, genre: 'lofi', bpm: [84, 88], levels: [1, 2], seconds: [38, 52], titles: ['Giz de Cera', 'Tarde Lenta', 'Gato Dormindo', 'Vinil Gasto', 'Lua de Papel'] },
  { n: 3, genre: 'lofi', bpm: [88, 94], levels: [1, 2], seconds: [40, 54], titles: ['Bruma', 'Folha Seca', 'Livro Velho', 'Neblina', 'Fim de Tarde'] },
  { n: 4, genre: 'house', bpm: [100, 106], levels: [2, 3], seconds: [42, 56], titles: ['Brisa Morna', 'Areia Branca', 'Maresia', 'Sol Poente', 'Onda Grande'] },
  { n: 5, genre: 'house', bpm: [104, 110], levels: [2, 3], seconds: [44, 56], titles: ['Rua Molhada', 'Farol Verde', 'Cais do Porto', 'Noite Clara', 'Mergulho'] },
  { n: 6, genre: 'house', bpm: [112, 116], levels: [3, 3], seconds: [48, 68], titles: ['Pista Cheia', 'Luz Negra', 'Globo Dourado', 'Passo Certo', 'Madrugada'] },
  { n: 7, genre: 'amapiano', bpm: [112, 115], levels: [3, 3], seconds: [50, 72], titles: ['Tambor Grave', 'Poeira Dourada', 'Calor de Janeiro', 'Sombra Fresca', 'Roda Viva'] },
  { n: 8, genre: 'synthwave', bpm: [114, 118], levels: [3, 4], seconds: [54, 76], titles: ['Neon Rosa', 'Estrada Reta', 'Fita Cassete', 'Horizonte', 'Cromado'] },
  { n: 9, genre: 'house', bpm: [118, 124], levels: [4, 4], seconds: [56, 80], titles: ['Vidro Azul', 'Cidade Alta', 'Trem Noturno', 'Sinal Verde', 'Arranha-Céu'] },
  { n: 10, genre: 'ukg', bpm: [122, 126], levels: [4, 4], seconds: [60, 84], titles: ['Passo Dois', 'Garoa', 'Plataforma', 'Vagão', 'Contratempo'] },
  { n: 11, genre: 'house', bpm: [126, 128], levels: [4, 5], seconds: [62, 90], titles: ['Salto Alto', 'Espelho', 'Veludo', 'Cristal Fino', 'Coroa'] },
  { n: 12, genre: 'trap', bpm: [130, 134], levels: [5, 5], seconds: [66, 94], titles: ['Névoa Roxa', 'Asfalto', 'Cromo Frio', 'Vidro Fumê', 'Rei do Bairro'] },
  { n: 13, genre: 'speedgarage', bpm: [132, 138], levels: [5, 6], seconds: [70, 98], titles: ['Pulso', 'Contraluz', 'Estrobo', 'Sub Grave', 'Fio Desencapado'] },
  { n: 14, genre: 'trap', bpm: [136, 140], levels: [6, 6], seconds: [72, 102], titles: ['Sirene Azul', 'Beco', 'Luz de Poste', 'Cerol', 'Trono'] },
  { n: 15, genre: 'phonk', bpm: [142, 146], levels: [6, 7], seconds: [76, 106], titles: ['Drift Noturno', 'Fumaça', 'Borracha Queimada', 'Tanque Cheio', 'Linha de Chegada'] },
  { n: 16, genre: 'trap', bpm: [144, 150], levels: [7, 7], seconds: [80, 110], titles: ['Tempestade', 'Relâmpago', 'Trovão', 'Olho do Furacão', 'Céu Rachado'] },
  { n: 17, genre: 'brphonk', bpm: [150, 154], levels: [7, 8], seconds: [84, 116], titles: ['Grave Pesado', 'Baile Fantasma', 'Tamborzão', 'Motor Quente', 'Paredão'] },
  { n: 18, genre: 'brphonk', bpm: [154, 160], levels: [8, 8], seconds: [90, 120], titles: ['Ferro Quente', 'Cabo de Aço', 'Faísca', 'Curto-Circuito', 'Alta Tensão'] },
  { n: 19, genre: 'dnb', bpm: [162, 170], levels: [8, 9], seconds: [96, 126], titles: ['Queda Livre', 'Túnel', 'Velocidade', 'Hiperespaço', 'Cometa'] },
  { n: 20, genre: 'dnb', bpm: [170, 174], levels: [9, 9], seconds: [100, 135], titles: ['Supernova', 'Gravidade Zero', 'Órbita', 'Big Bang', 'Solta!'] },
];

const READY = new Set(TEMPLATES.map((t) => t.id));

export const PENDING_GENRES: readonly GenreId[] = [...new Set(WORLD_PLANS.map((w) => w.genre))].filter((g) => !READY.has(g));

export function seedOf(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const lerp = (a: number, b: number, k: number): number => a + (b - a) * k;

export function trackOf(plan: WorldPlan, index: number): LadderTrack {
  const boss = index === TRACKS_PER_WORLD - 1;
  const k = index / (TRACKS_PER_WORLD - 1);
  const bpm = Math.round(lerp(plan.bpm[0], plan.bpm[1], k));
  const level = boss ? plan.levels[1] : Math.round(lerp(plan.levels[0], plan.levels[1], k));
  const wanted = boss ? plan.seconds[1] : lerp(plan.seconds[0], plan.seconds[1] * 0.9, index / (TRACKS_PER_WORLD - 2));
  const energy = boss ? 0.9 : Math.round((0.55 + 0.08 * index) * 100) / 100;
  const bars = Math.min(barsOf({ bpm, seconds: wanted, energy }), barsOf({ bpm: plan.bpm[1], seconds: plan.seconds[1], energy }));
  const title = plan.titles[index]!;
  const seed = seedOf(title);
  return {
    world: plan.n,
    index,
    boss,
    genre: plan.genre,
    seed,
    title,
    bpm,
    bars,
    seconds: (bars * 240) / bpm,
    energy,
    level,
    spec: `${plan.genre}:${seed}:${String(bpm)}:${String(bars)}:${String(energy)}`,
    ready: READY.has(plan.genre),
  };
}

export const LADDER: readonly (readonly LadderTrack[])[] = WORLD_PLANS.map((w) => Array.from({ length: TRACKS_PER_WORLD }, (_, i) => trackOf(w, i)));

export const LADDER_TRACKS: readonly LadderTrack[] = LADDER.flat();
